import React, { useMemo } from "react";
import { Check, CheckCircle2, AlertOctagon, RotateCcw } from "lucide-react";
import { Button } from "@shared/ui/button";
import { QuickStatsCard } from "@shared/components/QuickStatsCard";
import { EntityTypeName, ImportSummary } from "../types";

interface DoneStepProps {
  entityType: EntityTypeName;
  summary: ImportSummary;
  onImportMore: (onGoToStep?: (index: number) => void) => void;
  onClose: () => void;
  onGoToStep?: (index: number) => void;
}

export const DoneStep: React.FC<DoneStepProps> = ({
  entityType,
  summary,
  onImportMore,
  onClose,
  onGoToStep,
}) => {
  // Array of KPI stats data objects for QuickStatsCard mapping
  const kpiStatsData = useMemo(
    () => [
      {
        title: "Imported",
        count: summary.importedRows,
        displayValue: summary.importedRows.toString(),
        className: "bg-green-50/30 border-green-200 text-green-600 shadow-none",
      },
      {
        title: "Skipped",
        count: summary.skippedRows,
        displayValue: summary.skippedRows.toString(),
        className: "bg-red-50/30 border-red-200 text-red-600 shadow-none",
      },
    ],
    [summary]
  );

  return (
    <div className="space-y-6 py-4">
      {/* Complete Banner Header */}
      <div className="flex flex-col items-center justify-center text-center space-y-2">
        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 mb-2 shadow-sm">
          <Check className="h-8 w-8 stroke-[3]" />
        </div>
        <h2 className="text-xl font-extrabold text-gray-900">Import Complete</h2>
        <p className="text-sm text-gray-500 font-medium">
          <span className="font-semibold text-gray-700">{entityType}</span> records
          have been processed.
        </p>
      </div>

      {/* KPI Cards mapped from kpiStatsData */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-md mx-auto">
        {kpiStatsData.map((statItem, idx) => (
          <QuickStatsCard key={idx} {...statItem} />
        ))}
      </div>

      {/* Notice when skipped rows exist */}
      {summary.skippedRows > 0 && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-center text-xs font-medium text-red-700 max-w-lg mx-auto">
          {summary.skippedRows} row{summary.skippedRows !== 1 ? "s were" : " was"} skipped due
          to missing required fields. Download the template, fix the errors, and
          re-import.
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200 mt-6">
        <Button variant="outline" onClick={onClose} className="px-6 font-medium">
          Close
        </Button>
        <Button
          onClick={() => onImportMore(onGoToStep)}
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-6"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          Import More
        </Button>
      </div>
    </div>
  );
};
